/**
 * IdleGuard - "Still there?" for an unattended kiosk.
 *
 * Every touch / key restarts the timer (the owner's kiosk setting
 * `idle_timeout`, seconds). When it runs out on an ordering screen the
 * customer gets 10 s to answer; then the order in progress is abandoned (only
 * when nothing was or may still be charged), the session is reset and the
 * kiosk goes back to its attract screen.
 *
 * Not on the attract / confirmation screens (they reset themselves), and never
 * while a card try is open or a payment result screen runs its own reset.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Hand } from 'lucide-react';
import { useKiosk } from '../../context/KioskContext';
import { canAbandon, kioskBase, kioskPath } from '../../utils/kioskPath';

const ANSWER_SECONDS = 10;

export default function IdleGuard() {
  const { idleTimeoutSeconds, paymentPhase, activePayment, abandonOrder, resetSession, lastTouchRef, cart } = useKiosk();
  const location = useLocation();
  const navigate = useNavigate();
  const [asking, setAsking] = useState(false);
  const [left, setLeft] = useState(ANSWER_SECONDS);
  const askingRef = useRef(false);

  const rest = location.pathname.slice(kioskBase().length).replace(/^\/+|\/+$/g, '');
  const watched = rest !== '' && rest !== 'confirmation' && rest !== 'setup' && paymentPhase === 'form';

  const touched = useCallback(() => {
    lastTouchRef.current = Date.now();
    if (askingRef.current) { askingRef.current = false; setAsking(false); }
  }, [lastTouchRef]);

  useEffect(() => {
    const events = ['pointerdown', 'keydown', 'touchstart', 'wheel'];
    events.forEach((e) => window.addEventListener(e, touched, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, touched));
  }, [touched]);

  // a new screen counts as activity
  useEffect(() => { lastTouchRef.current = Date.now(); }, [location.pathname, lastTouchRef]);

  useEffect(() => {
    if (!watched) { askingRef.current = false; setAsking(false); return undefined; }
    const timer = setInterval(() => {
      if (!askingRef.current && Date.now() - lastTouchRef.current >= idleTimeoutSeconds * 1000) {
        askingRef.current = true;
        setLeft(ANSWER_SECONDS);
        setAsking(true);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [watched, idleTimeoutSeconds, lastTouchRef]);

  const giveUp = useCallback(async () => {
    askingRef.current = false;
    setAsking(false);
    if (canAbandon(activePayment)) await abandonOrder(activePayment.order.order_id);
    resetSession();
    navigate(kioskPath(''), { replace: true });
  }, [activePayment, abandonOrder, resetSession, navigate]);

  useEffect(() => {
    if (!asking) return undefined;
    if (left <= 0) { giveUp(); return undefined; }
    const timer = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [asking, left, giveUp]);

  if (!asking || !watched) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-8" data-testid="kiosk-still-there">
      <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#1a1f26] p-10 text-center shadow-2xl">
        <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-[#f5a623]/10 flex items-center justify-center">
          <Hand size={44} className="text-[#f5a623]" />
        </div>
        <h2 className="text-4xl font-bold text-white mb-3">Still there?</h2>
        <p className="text-lg text-white/60 mb-8">
          {cart.length ? 'Your order will be cleared' : 'Starting over'} in <span className="text-white font-semibold" data-testid="kiosk-still-there-seconds">{left}</span> seconds.
        </p>
        <button type="button" onClick={touched}
          className="w-full min-h-[72px] rounded-2xl bg-[#f5a623] text-[#0f1419] text-xl font-semibold active:scale-[0.98]"
          data-testid="kiosk-still-here">
          I'm still here
        </button>
      </div>
    </div>
  );
}
